import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Card, 
  Switch, 
  Modal, 
  Form, 
  Input, 
  message, 
  Spin,
  Tooltip,
  Space,
  Typography
} from 'antd';
import { 
  DragDropContext, 
  Droppable, 
  Draggable, 
  DropResult 
} from 'react-beautiful-dnd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  PlusOutlined,
  HolderOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import HomeLayoutApi, { 
  LayoutOrder, 
  CreateSectionRequest, 
  UpdateSectionRequest 
} from '../../api/home-layout/home-layout.api';
import './home-layout.styles.scss';

const { Title, Text } = Typography;
const { TextArea } = Input;

const HomeLayoutPage: React.FC = () => {
  const [layout, setLayout] = useState<LayoutOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<LayoutOrder | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadLayout();
  }, []);

  const loadLayout = async () => {
    try {
      setLoading(true);
      HomeLayoutApi.getLayoutOrder().subscribe({
        next: (response: any) => {
          if (response?.data) {
            setLayout(response.data);
          }
        },
        error: (error) => {
          console.error('Failed to load layout:', error);
          message.error('Không thể tải layout');
        },
        complete: () => {
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('Failed to load layout:', error);
      message.error('Không thể tải layout');
      setLoading(false);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const newLayout = Array.from(layout);
    const [reorderedItem] = newLayout.splice(result.source.index, 1);
    newLayout.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers
    const updatedSections = newLayout.map((item, index) => ({
      sectionId: item.sectionId,
      order: index + 1,
      isVisible: item.isVisible,
      config: item.config,
    }));

    try {
      setSaving(true);
      HomeLayoutApi.updateLayoutOrder({ sections: updatedSections }).subscribe({
        next: () => {
          // Update local state with new order
          const updatedLayout = newLayout.map((item, index) => ({
            ...item,
            order: index + 1,
          }));
          setLayout(updatedLayout);
          message.success('Cập nhật thứ tự thành công');
        },
        error: (error) => {
          console.error('Failed to update layout order:', error);
          message.error('Không thể cập nhật thứ tự');
          // Revert on error
          loadLayout();
        },
        complete: () => {
          setSaving(false);
        }
      });
    } catch (error) {
      console.error('Failed to update layout order:', error);
      message.error('Không thể cập nhật thứ tự');
      setSaving(false);
      loadLayout();
    }
  };

  const toggleVisibility = async (sectionId: number) => {
    const updatedLayout = layout.map(item => 
      item.sectionId === sectionId 
        ? { ...item, isVisible: !item.isVisible }
        : item
    );

    const updatedSections = updatedLayout.map(item => ({
      sectionId: item.sectionId,
      order: item.order,
      isVisible: item.isVisible,
      config: item.config,
    }));

    try {
      setSaving(true);
      HomeLayoutApi.updateLayoutOrder({ sections: updatedSections }).subscribe({
        next: () => {
          setLayout(updatedLayout);
          message.success('Cập nhật hiển thị thành công');
        },
        error: (error) => {
          console.error('Failed to update visibility:', error);
          message.error('Không thể cập nhật hiển thị');
        },
        complete: () => {
          setSaving(false);
        }
      });
    } catch (error) {
      console.error('Failed to update visibility:', error);
      message.error('Không thể cập nhật hiển thị');
      setSaving(false);
    }
  };

  const initializeDefault = async () => {
    try {
      setSaving(true);
      HomeLayoutApi.initializeDefaultSections().subscribe({
        next: () => {
          message.success('Khởi tạo layout mặc định thành công');
          loadLayout();
        },
        error: (error) => {
          console.error('Failed to initialize default sections:', error);
          message.error('Không thể khởi tạo layout mặc định');
        },
        complete: () => {
          setSaving(false);
        }
      });
    } catch (error) {
      console.error('Failed to initialize default sections:', error);
      message.error('Không thể khởi tạo layout mặc định');
      setSaving(false);
    }
  };

  const handleCreateSection = () => {
    setEditingSection(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEditSection = (section: LayoutOrder) => {
    setEditingSection(section);
    form.setFieldsValue({
      name: section.section.name,
      displayName: section.section.displayName,
      description: section.section.description,
      config: JSON.stringify(section.config || {}, null, 2),
    });
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      let config = {};
      
      try {
        config = JSON.parse(values.config || '{}');
      } catch (error) {
        message.error('Config JSON không hợp lệ');
        return;
      }

      const sectionData = {
        name: values.name,
        displayName: values.displayName,
        description: values.description,
        config,
        isActive: true,
      };

      setSaving(true);

      if (editingSection) {
        // Update existing section
        HomeLayoutApi.updateSection(editingSection.section.id, sectionData).subscribe({
          next: () => {
            message.success('Cập nhật section thành công');
            setIsModalOpen(false);
            loadLayout();
          },
          error: (error) => {
            console.error('Failed to update section:', error);
            message.error('Không thể cập nhật section');
          },
          complete: () => {
            setSaving(false);
          }
        });
      } else {
        // Create new section
        HomeLayoutApi.createSection(sectionData as CreateSectionRequest).subscribe({
          next: () => {
            message.success('Tạo section thành công');
            setIsModalOpen(false);
            loadLayout();
          },
          error: (error) => {
            console.error('Failed to create section:', error);
            message.error('Không thể tạo section');
          },
          complete: () => {
            setSaving(false);
          }
        });
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDeleteSection = (section: LayoutOrder) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa section "${section.section.displayName}"?`,
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => {
        HomeLayoutApi.deleteSection(section.section.id).subscribe({
          next: () => {
            message.success('Xóa section thành công');
            loadLayout();
          },
          error: (error) => {
            console.error('Failed to delete section:', error);
            message.error('Không thể xóa section');
          }
        });
      }
    });
  };

  if (loading) {
    return (
      <div className="home-layout-loading">
        <Spin size="large" />
        <Text>Đang tải...</Text>
      </div>
    );
  }

  return (
    <motion.div
      className="home-layout-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="page-header">
        <Title level={2}>Quản lý Layout Trang chủ</Title>
        <Text type="secondary">
          Kéo thả để thay đổi thứ tự hiển thị
        </Text>
      </div>

      <div className="page-actions">
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateSection}
          >
            Tạo Section mới
          </Button>
          <Button 
            onClick={initializeDefault} 
            disabled={saving}
            loading={saving}
          >
            Khởi tạo mặc định
          </Button>
        </Space>
      </div>

      {saving && (
        <div className="saving-indicator">
          <Spin size="small" />
          <Text>Đang lưu thay đổi...</Text>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="sections-container"
            >
              {layout.map((item, index) => (
                <Draggable
                  key={item.id.toString()}
                  draggableId={item.id.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`section-card ${
                        snapshot.isDragging ? 'dragging' : ''
                      } ${!item.isVisible ? 'hidden-section' : ''}`}
                    >
                      <div className="section-header">
                        <div 
                          {...provided.dragHandleProps} 
                          className="drag-handle"
                        >
                          <HolderOutlined />
                          <div className="section-info">
                            <Title level={5} className="section-title">
                              #{item.order} {item.section.displayName}
                            </Title>
                            <Text type="secondary" className="section-name">
                              Section: {item.section.name}
                            </Text>
                          </div>
                        </div>
                        
                        <div className="section-controls">
                          <Tooltip title={item.isVisible ? 'Ẩn section' : 'Hiển thị section'}>
                            <Switch
                              checked={item.isVisible}
                              onChange={() => toggleVisibility(item.sectionId)}
                              checkedChildren={<EyeOutlined />}
                              unCheckedChildren={<EyeInvisibleOutlined />}
                            />
                          </Tooltip>
                          
                          <Space>
                            <Tooltip title="Chỉnh sửa">
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleEditSection(item)}
                              />
                            </Tooltip>
                            <Tooltip title="Xóa">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteSection(item)}
                              />
                            </Tooltip>
                          </Space>
                        </div>
                      </div>

                      {(item.section.description || (item.config && Object.keys(item.config).length > 0)) && (
                        <div className="section-content">
                          {item.section.description && (
                            <Text className="section-description">
                              {item.section.description}
                            </Text>
                          )}
                          
                          {item.config && Object.keys(item.config).length > 0 && (
                            <div className="section-config">
                              <Text strong>Config:</Text>
                              <pre className="config-json">
                                {JSON.stringify(item.config, null, 0)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Create/Edit Section Modal */}
      <Modal
        title={editingSection ? 'Chỉnh sửa Section' : 'Tạo Section mới'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleModalSubmit}
        confirmLoading={saving}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            config: JSON.stringify({ limit: 10, showMore: true, layout: 'horizontal' }, null, 2)
          }}
        >
          <Form.Item
            label="Tên Section (name)"
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên section' }]}
          >
            <Input placeholder="books, collections, news, authors..." />
          </Form.Item>

          <Form.Item
            label="Tên hiển thị"
            name="displayName"
            rules={[{ required: true, message: 'Vui lòng nhập tên hiển thị' }]}
          >
            <Input placeholder="Sách nổi bật, Tuyển tập, Tin tức..." />
          </Form.Item>

          <Form.Item
            label="Mô tả"
            name="description"
          >
            <TextArea 
              placeholder="Mô tả về section này..."
              rows={2}
            />
          </Form.Item>

          <Form.Item
            label="Cấu hình (JSON)"
            name="config"
            rules={[
              {
                validator: (_, value) => {
                  try {
                    JSON.parse(value || '{}');
                    return Promise.resolve();
                  } catch {
                    return Promise.reject('Config phải là JSON hợp lệ');
                  }
                }
              }
            ]}
          >
            <TextArea
              placeholder='{"limit": 10, "showMore": true, "layout": "horizontal"}'
              rows={4}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </motion.div>
  );
};

export default HomeLayoutPage;
